import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import {
  __,
  convertHoursAndMinutesToMinutes,
  convertMinutesToHoursAndMinutes,
  getActiveProject,
  getActiveStage,
  validateForm,
} from '../../core/utils';
import { setNotification } from '../../redux/actions/general/notification';
import {
  hideAlertbox,
  showAlertbox,
} from '../../redux/actions/general/alertbox';
import { createSidebar } from '../../redux/actions/general/sidebar';
import { listActiveModules } from '../../redux/actions/general/active_modules';
import {
  createTimeOff,
  deleteUserLeave,
  readUserLeave,
  readTimesheet,
  editTimesheet,
  editUserLeave,
  listWeekViewParticipants,
} from '../../redux/actions/table/timesheets';
import { readCompanyHolidaysPeriod } from '../../redux/actions/table/company_holidays';
import {
  copyPreviousWeekUserWeekView,
  createUserWeekViewItem,
  deleteUserWeekViewItem,
  readUserWeekView,
  readUserWeekViewStatistics,
  readUserWeekViewTimeOff,
} from '../../redux/actions/table/timesheet_entries';
import styles from '../../styles/views/timesheets/TimesheetEntries.scss';
import TimesheetEntriesHeader from './components/TimesheetEntriesHeader';
import TimesheetEntriesStatistic from './components/TimesheetEntriesStatistic';
import Tooltip from '../../components/Tooltip';
import AddTimesheetEntryWorkItemForm from '../../forms/timesheets/AddTimesheetEntryWorkItemForm';
import AddTimesheetEntryTimeOffForm from '../../forms/timesheets/AddTimesheetEntryTimeOffForm';
import EditTimesheetEntryTimeOffForm from '../../forms/timesheets/EditTimesheetEntryTimeOffForm';
import EditTimesheetEntryWorkItemForm from '../../forms/timesheets/EditTimesheetEntryWorkItemForm';
import ArrowDownSmallIcon from '../../assets/images/arrow-down-small-15x15.svg';
import DeleteIcon from '../../assets/images/delete-15x15.svg';
import EditIcon from '../../assets/images/edit-16x16.svg';
import MenuHIcon from '../../assets/images/menu-h-15x15.svg';

const getTranslatedMonthShort = (date) => {
  const monthKeys = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
  ];

  return __(
    `timesheet_entries.header.month_short.${monthKeys[moment(date).month()]}`,
  );
};

const getTranslatedWeekdayShort = (date) => {
  const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  return __(`timesheet_entries.table.day_short.${dayKeys[moment(date).day()]}`);
};

const formatMinutes = (minutes) => {
  const safeMinutes = Number(minutes) || 0;
  const hours = Math.floor(safeMinutes / 60);
  const mins = Math.abs(safeMinutes % 60);

  return `${hours}:${String(mins).padStart(2, '0')}`;
};

const formatMinutesWithLeadingHour = (minutes) => {
  const safeMinutes = Number(minutes) || 0;
  const hours = Math.floor(safeMinutes / 60);
  const mins = Math.abs(safeMinutes % 60);

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatInlineTimeInput = (value) => {
  return String(value || '')
    .replace(/[^\d:]/g, '')
    .replace(/:{2,}/g, ':')
    .replace(/^:/, '')
    .slice(0, 5);
};

const parseInlineTimeInputToMinutes = (value) => {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return null;
  }

  const hhmmMatch = normalizedValue.match(/^(\d{1,2}):(\d{1,2})$/);

  if (hhmmMatch) {
    const hours = Number(hhmmMatch[1]);
    const minutes = Number(hhmmMatch[2]);

    if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59) {
      return null;
    }

    return hours * 60 + minutes;
  }

  const digitsOnly = normalizedValue.replace(/\D/g, '').slice(0, 4);

  if (!digitsOnly) {
    return null;
  }

  if (digitsOnly.length <= 2) {
    return Number(digitsOnly) * 60;
  }

  const hours = Number(digitsOnly.slice(0, -2));
  const minutes = Number(digitsOnly.slice(-2));

  if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
};

const formatInlineMinutesValue = (minutes) => {
  const safeMinutes = Number(minutes) || 0;
  const hours = Math.floor(safeMinutes / 60);
  const mins = Math.abs(safeMinutes % 60);

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatRange = (weekStart) => {
  const weekEnd = moment(weekStart).clone().endOf('isoWeek');
  return `${moment(weekStart).format('D')} ${getTranslatedMonthShort(
    weekStart,
  )} - ${weekEnd.format('D')} ${getTranslatedMonthShort(weekEnd)}`;
};

const buildVisibleDays = (weekStart, mode) => {
  const count = mode === '1d' ? 1 : mode === '5d' ? 5 : 7;
  return _.range(0, count).map((offset) =>
    moment(weekStart).clone().add(offset, 'days'),
  );
};

const getVisibleIndexes = (mode) => {
  if (mode === '1d') {
    return [0];
  }

  if (mode === '5d') {
    return [0, 1, 2, 3, 4];
  }

  return [0, 1, 2, 3, 4, 5, 6];
};

const sumItemMinutes = (items) =>
  _.sumBy(items, (item) => _.sum(item.days || []) || 0);

const parseDurationToMinutes = (value) => {
  if (_.isNil(value) || value === 'null') {
    return 0;
  }

  if (_.isNumber(value)) {
    return value;
  }

  if (_.isString(value)) {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return 0;
    }

    const hoursMinutesMatch = trimmedValue.match(/^(-?\d+):(\d{1,2})$/);

    if (hoursMinutesMatch) {
      const hours = Number(hoursMinutesMatch[1]);
      const minutes = Number(hoursMinutesMatch[2]);
      const sign = hours < 0 ? -1 : 1;

      return hours * 60 + sign * minutes;
    }

    const numericValue = Number(trimmedValue);

    if (!Number.isNaN(numericValue)) {
      return numericValue;
    }
  }

  if (_.isPlainObject(value)) {
    return parseDurationToMinutes(
      value.minutes || value.duration || value.value,
    );
  }

  return 0;
};

const getCollectionByDate = (collection, dateKey, index) => {
  if (_.isArray(collection)) {
    const arrayItem = collection[index];

    if (_.isPlainObject(arrayItem)) {
      return parseDurationToMinutes(
        arrayItem.minutes || arrayItem.duration || arrayItem.value,
      );
    }

    return parseDurationToMinutes(arrayItem);
  }

  if (_.isPlainObject(collection)) {
    return parseDurationToMinutes(
      collection[dateKey] ??
        collection[index] ??
        collection[String(index)] ??
        collection[moment(dateKey).format('YYYY-MM-DDTHH:mm:ss')],
    );
  }

  return 0;
};

const getItemMinutesForDay = (item, day, index) => {
  const dateKey = day.format('YYYY-MM-DD');
  const nestedCollections = [
    item?.days,
    item?.daily,
    item?.daily_totals,
    item?.minutes_per_date,
    item?.minutes_by_day,
    item?.entries_by_day,
    item?.values,
  ];

  for (const collection of nestedCollections) {
    const minutes = getCollectionByDate(collection, dateKey, index);

    if (minutes) {
      return minutes;
    }
  }

  if (_.isArray(item?.entries)) {
    return _.sumBy(item.entries, (entry) => {
      const entryDate =
        entry?.date || entry?.day || entry?.worked_at || entry?.start_date;

      return entryDate && moment(entryDate).format('YYYY-MM-DD') === dateKey
        ? parseDurationToMinutes(
            entry?.minutes || entry?.duration || entry?.logged_time,
          )
        : 0;
    });
  }

  const singleDate = item?.date || item?.day || item?.worked_at;

  if (singleDate && moment(singleDate).format('YYYY-MM-DD') === dateKey) {
    return parseDurationToMinutes(
      item?.minutes || item?.duration || item?.logged_time,
    );
  }

  return 0;
};

const getEntryDateKey = (entry) => {
  const entryDate =
    entry?.date || entry?.day || entry?.worked_at || entry?.start_date;

  return entryDate ? moment(entryDate).format('YYYY-MM-DD') : null;
};

const normalizeEntryCandidate = (candidate, dateKey) => {
  if (_.isArray(candidate)) {
    return (
      _.find(candidate, (entry) => getEntryDateKey(entry) === dateKey) ||
      _.find(candidate, (entry) => _.isPlainObject(entry)) ||
      null
    );
  }

  if (_.isPlainObject(candidate)) {
    if (_.isArray(candidate.entries)) {
      return normalizeEntryCandidate(candidate.entries, dateKey);
    }

    if (_.isPlainObject(candidate.entry)) {
      return candidate.entry;
    }

    return candidate;
  }

  return null;
};

const getEntryCandidateByDate = (collection, dateKey, index) => {
  if (_.isArray(collection)) {
    return normalizeEntryCandidate(collection[index], dateKey);
  }

  if (_.isPlainObject(collection)) {
    return normalizeEntryCandidate(
      collection[dateKey] ??
        collection[index] ??
        collection[String(index)] ??
        collection[moment(dateKey).format('YYYY-MM-DDTHH:mm:ss')],
      dateKey,
    );
  }

  return null;
};

const getWorkItemEntryForDay = (item, day, index) => {
  const dateKey = day.format('YYYY-MM-DD');

  if (_.isArray(item?.entries)) {
    const matchingEntry = _.find(
      item.entries,
      (entry) => getEntryDateKey(entry) === dateKey,
    );

    if (matchingEntry) {
      return matchingEntry;
    }
  }

  const nestedCollections = [
    item?.entries_by_day,
    item?.minutes_per_date,
    item?.minutes_by_day,
    item?.values,
    item?.daily,
    item?.daily_totals,
    item?.days,
  ];

  for (const collection of nestedCollections) {
    const candidate = getEntryCandidateByDate(collection, dateKey, index);

    if (candidate) {
      const candidateDateKey = getEntryDateKey(candidate);

      if (!candidateDateKey || candidateDateKey === dateKey) {
        return candidate;
      }
    }
  }

  if (getEntryDateKey(item) === dateKey) {
    return item;
  }

  return null;
};

const getWorkItemEntryIdForDay = (item, day, index) => {
  const entry = getWorkItemEntryForDay(item, day, index);

  return entry?.id;
};

const getWorkItemMultipleEntriesForDay = (item, day, index) => {
  const entry = getWorkItemEntryForDay(item, day, index);

  return Boolean(entry?.multiple_entries);
};

const getWorkItemMinutesForEditDay = (item, day, index) => {
  const dateKey = day.format('YYYY-MM-DD');

  // Week-view work rows expose exact day values here, so prefer this source
  // for the edit modal over aggregate row-level minutes.
  if (_.isPlainObject(item?.minutes_per_date)) {
    const dayMinutes = parseDurationToMinutes(item.minutes_per_date[dateKey]);

    if (!_.isNil(item.minutes_per_date[dateKey])) {
      return dayMinutes;
    }
  }

  return getItemMinutesForDay(item, day, index);
};

const getTimeOffEntryUuidForDay = (item, day) => {
  const dateKey = day.format('YYYY-MM-DD');
  return item?.minutes_per_date?.[dateKey]?.leave_uuid;
};

const getTimeOffMultipleEntriesForDay = (item, day) => {
  const dateKey = day.format('YYYY-MM-DD');

  return Boolean(item?.minutes_per_date?.[dateKey]?.multiple_entries);
};

const isZeroLikeIdentifier = (value) =>
  _.isNil(value) || String(value) === '0' || String(value) === 'null';

const getSavedEntryFromAction = (action, dateKey) => {
  const responseData = action?.response?.data ?? action?.response;

  if (_.isArray(responseData)) {
    return (
      _.find(
        responseData,
        (entry) =>
          entry?.date &&
          moment(entry.date).format('YYYY-MM-DD') === dateKey,
      ) || _.first(responseData)
    );
  }

  return responseData;
};

const updateItemDay = (
  item,
  dateKey,
  index,
  minutes,
  identifierField,
  identifier,
) => {
  const currentDateValue = item?.minutes_per_date?.[dateKey];
  const nextDays = [...(item.days || [])];

  nextDays[index] = minutes;

  return {
    ...item,
    days: nextDays,
    minutes_per_date: {
      ...(item.minutes_per_date || {}),
      [dateKey]: {
        ...(_.isPlainObject(currentDateValue) ? currentDateValue : {}),
        [identifierField]: identifier,
        minutes,
        multiple_entries: false,
      },
    },
  };
};

const updateItemDayIdentifier = (
  item,
  dateKey,
  identifierField,
  identifier,
) => ({
  ...item,
  minutes_per_date: {
    ...(item.minutes_per_date || {}),
    [dateKey]: {
      ...(_.isPlainObject(item?.minutes_per_date?.[dateKey])
        ? item.minutes_per_date[dateKey]
        : {}),
      [identifierField]: identifier,
    },
  },
});

const buildProjectStageLabel = (item) => {
  return (
    <div className={styles.projectStage}>
      <span>{item?.project_name}</span>
      <span>{item?.stage_name}</span>
    </div>
  );
};

const buildSubstageTaskLabel = (item) => {
  if (item.substage_id && item.task_id) {
    return (
      <div className={styles.substageTask}>
        <span>{item.substage_name}</span>
        <span>{item.task_name}</span>
      </div>
    );
  }

  return item?.substage_name || item?.task_name || '-';
};

const normalizeTimeWorkItems = (responseData, visibleDays) => {
  const rows = _.isArray(responseData)
    ? _.flatMap(responseData, (workPlan, workPlanIndex) =>
        _.map(workPlan?.items || [], (item, itemIndex) => ({
          ...workPlan,
          ...item,
          id:
            item?.id ||
            item?.uuid ||
            `week-plan-item-${workPlan?.id || workPlanIndex}-${itemIndex}`,
          work_plan_id: item?.work_plan_id || workPlan?.id,
        })),
      )
    : responseData?.time_work ||
      responseData?.time?.work ||
      responseData?.work_items ||
      responseData?.entries ||
      responseData?.data ||
      [];

  if (!_.isArray(rows)) {
    return [];
  }

  return rows.map((item, rowIndex) => ({
    id: item?.id || item?.uuid || `week-view-row-${rowIndex}`,
    week_view_item_id: item?.id || item?.uuid || null,
    multiple_entries: Boolean(item?.multiple_entries),
    project_id: item?.project_id,
    stage_id: item?.stage_id,
    substage_id: item?.substage_id,
    task_id: item?.task_id,
    project_name: item?.project_name,
    stage_name: item?.stage_name,
    substage_name: item?.substage_name,
    task_name: item?.task_name,
    description: item?.description,
    address_book_company_id: item?.address_book_company_id,
    color: item?.color || (item?.billable === false ? 'green' : 'blue'),
    projectStage: buildProjectStageLabel(item),
    substageTask: buildSubstageTaskLabel(item),
    billable: item?.billable !== false,
    date: item?.date,
    day: item?.day,
    worked_at: item?.worked_at,
    start_date: item?.start_date,
    entries: item?.entries,
    entries_by_day: item?.entries_by_day,
    daily: item?.daily,
    daily_totals: item?.daily_totals,
    minutes_per_date: item?.minutes_per_date,
    minutes_by_day: item?.minutes_by_day,
    values: item?.values,
    days: visibleDays.map((day, dayIndex) =>
      getItemMinutesForDay(item, day, dayIndex),
    ),
  }));
};

const normalizeTimeOffItems = (responseData, visibleDays) => {
  const rows = _.isArray(responseData?.items)
    ? _.map(responseData.items, (item) => ({
        ...responseData,
        ...item,
        id: item?.leave_uuid,
      }))
    : responseData?.time_off ||
      responseData?.time?.off ||
      responseData?.off_items ||
      responseData?.entries ||
      responseData?.data ||
      [];

  if (!_.isArray(rows)) {
    return [];
  }

  return rows.map((item) => ({
    id: item?.leave_uuid,
    actionKey: `timeoff-row-${item?.leave_uuid}`,
    leave_uuid: item?.leave_uuid,
    multiple_entries: Boolean(item?.multiple_entries),
    leave_reason_id: item?.leave_reason_id,
    all_day: item?.all_day,
    description: item?.description,
    minutes_per_date: item?.minutes_per_date,
    color: item?.color || 'red',
    projectStage:
      item?.leave_reason_name ||
      item?.reason_name ||
      item?.projectStage ||
      buildProjectStageLabel(item),
    substageTask: item?.description || item?.substageTask || '-',
    billable: false,
    days: visibleDays.map((day, dayIndex) =>
      getItemMinutesForDay(item, day, dayIndex),
    ),
  }));
};

const normalizeHolidayMap = (responseData) => {
  if (!_.isArray(responseData)) {
    return {};
  }

  return _.reduce(
    responseData,
    (result, item) => {
      if (item?.date) {
        result[item.date] = {
          holiday: Boolean(item?.holiday),
          holiday_name: item?.holiday_name || null,
        };
      }

      return result;
    },
    {},
  );
};

const TimesheetEntries = ({ auth }) => {
  const viewModes = useMemo(
    () => [
      // { value: '1d', label: '1D' },
      { value: '5d', label: __('timesheet_entries.header.view_mode_5d') },
      { value: '7d', label: __('timesheet_entries.header.view_mode_7d') },
    ],
    [],
  );

  const [possibleUsers, setPossibleUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [viewMode, setViewMode] = useState('5d');
  const [weekStart, setWeekStart] = useState(moment().startOf('isoWeek'));
  const [collapsedSections, setCollapsedSections] = useState([]);
  const [showAddWorkItemModal, setShowAddWorkItemModal] = useState(false);
  const [showAddTimeOffModal, setShowAddTimeOffModal] = useState(false);
  const [workItems, setWorkItems] = useState([]);
  const [timeOffItems, setTimeOffItems] = useState([]);
  const [statisticsData, setStatisticsData] = useState(null);
  const [holidayMap, setHolidayMap] = useState({});
  const [cellDrafts, setCellDrafts] = useState({});
  const [actionedRowId, setActionedRowId] = useState(null);
  const [showEditWorkItemModal, setShowEditWorkItemModal] = useState(false);
  const [editWorkItemData, setEditWorkItemData] = useState(null);
  const [editWorkItemDate, setEditWorkItemDate] = useState(null);
  const [editWorkItemMinutes, setEditWorkItemMinutes] = useState(null);
  const [showEditTimeOffModal, setShowEditTimeOffModal] = useState(false);
  const [editTimeOffData, setEditTimeOffData] = useState(null);
  const [editTimeOffItem, setEditTimeOffItem] = useState(null);
  const [editTimeOffDate, setEditTimeOffDate] = useState(null);
  const [editTimeOffMinutes, setEditTimeOffMinutes] = useState(null);
  const activeProject = getActiveProject();
  const activeStage = getActiveStage();

  useEffect(() => {
    createSidebar({
      title: __('submenu.title.time-log-main'),
      items: [
        {
          title: __('submenu.title.time-log-submodule'),
          link: () => '/timesheets',
        },
        {
          title: __('submenu.title.all-timesheet-reports'),
          link: () => '/timesheet_report',
        },
        {
          title: __('Timesheet Entries'),
          link: () => '/timesheet_entries',
          active: true,
        },
      ],
    });

    listActiveModules();

    listWeekViewParticipants().then(({ response }) => {
      const users = _.sortBy(
        _.map(response.data, (row) => ({
          value: row.id,
          label: row.fullname,
        })),
        (user) => _.toLower(user.label),
      );

      setPossibleUsers(users);

      if (_.isNil(selectedUserId) && _.size(users)) {
        const loggedUserOption = _.find(users, ['value', auth?.id]);

        setSelectedUserId(
          _.get(
            loggedUserOption,
            'value',
            _.get(_.first(users), 'value', null),
          ),
        );
      }
    });
  }, []);

  const visibleIndexes = useMemo(() => getVisibleIndexes(viewMode), [viewMode]);
  const visibleDays = useMemo(
    () => buildVisibleDays(weekStart, viewMode),
    [viewMode, weekStart],
  );
  const activeDateRange = useMemo(
    () => ({
      start: _.get(visibleDays, '[0]', moment()).format('YYYY-MM-DD'),
      end: _.get(visibleDays, `[${visibleDays.length - 1}]`, moment()).format(
        'YYYY-MM-DD',
      ),
    }),
    [visibleDays],
  );

  const loadWeekView = useCallback(() => {
    const start_date = visibleDays[0]?.format('YYYY-MM-DD');
    const end_date =
      viewMode === '1d'
        ? undefined
        : visibleDays[visibleDays.length - 1]?.format('YYYY-MM-DD');
    const holiday_end_date =
      visibleDays[visibleDays.length - 1]?.format('YYYY-MM-DD') || start_date;
    const user_id = Number(selectedUserId) || Number(auth?.id);

    if (!start_date) {
      return Promise.resolve();
    }

    return Promise.all([
      readUserWeekView({ user_id, start_date, end_date }),
      readUserWeekViewTimeOff({ user_id, start_date, end_date }),
      readUserWeekViewStatistics({ user_id, start_date, end_date }),
      readCompanyHolidaysPeriod({
        date_from: start_date,
        date_to: holiday_end_date,
      }),
    ]).then(
      ([
        workAction,
        timeOffAction,
        statisticsAction,
        companyHolidaysAction,
      ]) => {
        const workResponseData =
          workAction?.response?.data || workAction?.response || {};
        const timeOffResponseData =
          timeOffAction?.response?.data || timeOffAction?.response || {};
        const statisticsResponseData =
          statisticsAction?.response?.data || statisticsAction?.response || {};
        const companyHolidaysResponseData =
          companyHolidaysAction?.response?.data ||
          companyHolidaysAction?.response ||
          [];
        const normalizedWorkItems = normalizeTimeWorkItems(
          workResponseData,
          visibleDays,
        );
        const normalizedTimeOffItems = normalizeTimeOffItems(
          timeOffResponseData,
          visibleDays,
        );

        setWorkItems(normalizedWorkItems);
        setTimeOffItems(normalizedTimeOffItems);
        setStatisticsData(statisticsResponseData);
        setHolidayMap(normalizeHolidayMap(companyHolidaysResponseData));
      },
    );
  }, [auth?.id, selectedUserId, viewMode, visibleDays]);

  const refreshWeekViewStatistics = useCallback(() => {
    return readUserWeekViewStatistics({
      user_id: Number(selectedUserId) || Number(auth?.id),
      start_date: activeDateRange.start,
      end_date: activeDateRange.end,
    })
      .then((statisticsAction) => {
        setStatisticsData(
          statisticsAction?.response?.data || statisticsAction?.response || {},
        );
      })
      .catch((failureAction) => {
        setNotification({
          type: 'warning',
          text:
            failureAction?.response?.error ||
            __('Timesheet statistics could not be refreshed.'),
        });
      });
  }, [activeDateRange, auth?.id, selectedUserId]);

  useEffect(() => {
    loadWeekView();
  }, [loadWeekView]);

  useEffect(() => {
    if (_.isNil(actionedRowId)) {
      return undefined;
    }

    const handleDocumentClick = () => {
      setActionedRowId(null);
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [actionedRowId]);

  const dailyTotals = useMemo(
    () =>
      visibleIndexes.map((index) =>
        _.sumBy(workItems, (item) => item.days?.[index] || 0),
      ),
    [visibleIndexes, workItems],
  );

  const tableGridStyle = useMemo(
    () => ({
      gridTemplateColumns: `minmax(240px, 1.2fr) minmax(220px, 1.2fr) repeat(${visibleIndexes.length}, minmax(88px, 0.7fr)) 88px 74px`,
      minWidth: `${240 + 220 + visibleIndexes.length * 88 + 88 + 74}px`,
    }),
    [visibleIndexes.length],
  );
  const currentWeekNumber = moment(weekStart).isoWeek();
  const holidayColumns = useMemo(
    () =>
      _.map(visibleDays, (day) =>
        Boolean(holidayMap?.[day.format('YYYY-MM-DD')]?.holiday),
      ),
    [holidayMap, visibleDays],
  );

  const toggleSection = (section) => {
    setCollapsedSections((current) =>
      _.includes(current, section)
        ? _.without(current, section)
        : [...current, section],
    );
  };

  const addWorkItem = () => {
    setShowAddWorkItemModal(true);
  };

  const addTimeOff = () => {
    setShowAddTimeOffModal(true);
  };

  const closeEditWorkItemModal = () => {
    setShowEditWorkItemModal(false);
    setEditWorkItemData(null);
    setEditWorkItemDate(null);
    setEditWorkItemMinutes(null);
  };

  const closeEditTimeOffModal = () => {
    setShowEditTimeOffModal(false);
    setEditTimeOffData(null);
    setEditTimeOffItem(null);
    setEditTimeOffDate(null);
    setEditTimeOffMinutes(null);
  };

  const handleCreateWorkItem = (formData) => {
    return createUserWeekViewItem({
      user_id: Number(selectedUserId) || Number(auth?.id),
      start_date: activeDateRange.start,
      end_date: activeDateRange.end,
      items: [
        {
          project_id: formData.project_id,
          task_id: formData.task_id || null,
          stage_id: formData.stage_id,
          substage_id: formData.substage_id || null,
        },
      ],
    }).then(() => {
      setShowAddWorkItemModal(false);
      return loadWeekView();
    });
  };

  const handleCreateTimeOffItem = (formData) => {
    const payload = {
      user_id: Number(selectedUserId) || Number(auth?.id),
      reason: formData.reason,
      description: formData.description,
      all_day: formData?.all_day ? formData.all_day : 0,
      ...(formData?.all_day
        ? {
            start_time: formData?.date?.start,
            end_time: formData?.date?.end,
          }
        : {
            date: formData?.date,
            minutes: convertHoursAndMinutesToMinutes(
              formData?.hours,
              formData?.minutes,
            ),
          }),
    };

    return validateForm(
      createTimeOff({
        ...payload,
      }).then(() => {
        setShowAddTimeOffModal(false);
        return loadWeekView();
      }),
    );
  };

  const handleCopyPreviousWeek = () => {
    return copyPreviousWeekUserWeekView({
      user_id: Number(selectedUserId) || Number(auth?.id),
      start_date: activeDateRange.start,
      end_date: activeDateRange.end,
    })
      .then(() => loadWeekView())
      .catch(() => {
        setNotification({
          type: 'warning',
          text: __('timesheet_entries.notification.copy_previous_week_failed'),
        });
      });
  };

  const handleOpenEditWorkItemModal = (item, day, index) => {
    if (getWorkItemMultipleEntriesForDay(item, day, index)) {
      return;
    }

    const entryId = getWorkItemEntryIdForDay(item, day, index);

    if (_.isNil(entryId)) {
      return;
    }

    setEditWorkItemDate(day.format('YYYY-MM-DD'));
    setEditWorkItemMinutes(getWorkItemMinutesForEditDay(item, day, index));
    setEditWorkItemData(null);

    if (isZeroLikeIdentifier(entryId)) {
      setEditWorkItemData({
        id: entryId,
        user_id: Number(selectedUserId) || Number(auth?.id),
        date: day.format('YYYY-MM-DD'),
        description: item?.description || '',
        address_book_company_id: item?.address_book_company_id || null,
        is_billable: item?.billable ? 1 : 0,
        project_id: item?.project_id || null,
        stage_id: item?.stage_id || null,
        task_id: item?.task_id || null,
        substage_id: item?.substage_id || null,
      });
      setShowEditWorkItemModal(true);
      return;
    }

    readTimesheet(entryId).then(({ response }) => {
      setEditWorkItemData(response?.data || response || null);
      setShowEditWorkItemModal(true);
    });
  };

  const handleOpenEditTimeOffModal = (item, day, index, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (getTimeOffMultipleEntriesForDay(item, day)) {
      return;
    }

    const entryUuid = getTimeOffEntryUuidForDay(item, day);

    setEditTimeOffItem(item);
    setEditTimeOffDate(day.format('YYYY-MM-DD'));
    setEditTimeOffMinutes(item.days?.[index] || 0);
    setEditTimeOffData(null);

    if (isZeroLikeIdentifier(entryUuid)) {
      const currentDate = day.format('YYYY-MM-DD');

      setEditTimeOffData({
        leave_uuid: entryUuid,
        user_id: Number(selectedUserId) || Number(auth?.id),
        leave_reason_id: item?.leave_reason_id || null,
        description: item?.description || '',
        date: currentDate,
        start_time: currentDate,
        end_time: currentDate,
        minutes: item.days?.[index] || 0,
      });
      setShowEditTimeOffModal(true);
      return;
    }

    readUserLeave(entryUuid).then(({ response }) => {
      setEditTimeOffData(response?.data || response || null);
      setShowEditTimeOffModal(true);
    });
  };

  const editWorkItemInitialValues = useMemo(() => {
    if (!editWorkItemData) {
      return null;
    }

    const { hours, minutes } = convertMinutesToHoursAndMinutes(
      _.isNumber(editWorkItemMinutes)
        ? editWorkItemMinutes
        : editWorkItemData?.minutes || 0,
    );

    return {
      ..._.pick(editWorkItemData, [
        'id',
        'user_id',
        'date',
        'description',
        'address_book_company_id',
        'is_billable',
        'project_id',
        'stage_id',
        'task_id',
        'substage_id',
      ]),
      date: editWorkItemDate || editWorkItemData?.date,
      minutes,
      hours,
    };
  }, [editWorkItemData, editWorkItemDate, editWorkItemMinutes]);

  const editTimeOffInitialValues = useMemo(() => {
    if (!editTimeOffData || !editTimeOffItem) {
      return null;
    }

    const isAllDay = Number(editTimeOffItem?.all_day) === 1;
    const { hours, minutes } = convertMinutesToHoursAndMinutes(
      _.isNumber(editTimeOffMinutes)
        ? editTimeOffMinutes
        : editTimeOffData?.minutes || 0,
    );
    const startTime = editTimeOffData?.start_time || editTimeOffData?.date;
    const endTime =
      editTimeOffData?.end_time || editTimeOffData?.start_time || startTime;
    const isMultiDay =
      Boolean(startTime) &&
      Boolean(endTime) &&
      moment(startTime).format('YYYY-MM-DD') !==
        moment(endTime).format('YYYY-MM-DD');

    return {
      leave_reason_id: editTimeOffData?.leave_reason_id,
      description: editTimeOffData?.description,
      all_day: isAllDay ? 1 : 0,
      is_multi_day: isMultiDay,
      date: editTimeOffDate || editTimeOffData?.date,
      hours,
      minutes,
      date_display: startTime,
      date_range: {
        start: startTime,
        end: endTime,
      },
    };
  }, [editTimeOffData, editTimeOffDate, editTimeOffItem, editTimeOffMinutes]);

  const handleEditWorkItem = (formData) => {
    const payload = {
      ...formData,
      user_id:
        formData.user_id ||
        editWorkItemData?.user_id ||
        Number(selectedUserId) ||
        Number(auth?.id),
      minutes: convertHoursAndMinutesToMinutes(
        formData?.hours,
        formData?.minutes,
      ),
    };

    return validateForm(
      editTimesheet(formData.id, _.omit(payload, ['id', 'hours'])).then(() => {
        closeEditWorkItemModal();
        return loadWeekView();
      }),
    );
  };

  const handleEditTimeOff = (formData) => {
    const isAllDay = Number(editTimeOffItem?.all_day) === 1;
    const entryUuid = getTimeOffEntryUuidForDay(
      editTimeOffItem,
      moment(editTimeOffDate),
    );
    const payload = {
      user_id:
        editTimeOffData?.user_id || Number(selectedUserId) || Number(auth?.id),
      reason: formData?.leave_reason_id || '',
      description: formData?.description || '',
      ...(isAllDay
        ? {
            start_time:
              editTimeOffData?.start_time ||
              formData?.date_range?.start ||
              formData?.date_display,
            end_time:
              editTimeOffData?.end_time ||
              formData?.date_range?.end ||
              formData?.date_display,
            minutes: null,
            date: null,
          }
        : {
            date: formData?.date || editTimeOffDate,
            minutes: convertHoursAndMinutesToMinutes(
              formData?.hours,
              formData?.minutes,
            ),
          }),
    };

    return validateForm(
      editUserLeave(entryUuid, payload).then(() => {
        closeEditTimeOffModal();
        return loadWeekView();
      }),
    );
  };

  const handleDeleteEntry = ({
    deleteAction,
    deleteId,
    successMessage,
    fallbackMessage,
  }) => {
    if (!deleteId) {
      setNotification(fallbackMessage);
      return;
    }

    showAlertbox({
      color: 'red',
      title: __('general.alert.are-you-sure'),
      description: __(
        'table.timesheets.actions.delete.tooltip.this-process-cannot-undone',
      ),
      buttons: [
        {
          color: 'lightGray',
          text: __('general.alert.no-close'),
          onClick: 'close',
        },
        {
          color: 'gray',
          text: __('general.alert.yes-delete'),
          onClick: () => {
            deleteAction(deleteId).then(() => {
              hideAlertbox();
              setActionedRowId(null);
              setNotification(successMessage);
              loadWeekView();
            });
          },
        },
      ],
    });
  };

  const handleDeleteWorkItem = (item) => {
    handleDeleteEntry({
      deleteAction: deleteUserWeekViewItem,
      deleteId: item?.week_view_item_id,
      successMessage: __('Work time entry deleted.'),
      fallbackMessage: __('Work time entry cannot be deleted.'),
    });
  };

  const handleDeleteTimeOffItem = (item) => {
    handleDeleteEntry({
      deleteAction: deleteUserLeave,
      deleteId: item?.leave_uuid,
      successMessage: __('Time off entry deleted.'),
      fallbackMessage: __('Time off entry cannot be deleted.'),
    });
  };

  const toggleRowActions = (rowId, event) => {
    event.preventDefault();
    event.stopPropagation();
    setActionedRowId((current) => (current === rowId ? null : rowId));
  };

  const handleCellDraftChange = (rowId, dateKey, value) => {
    setCellDrafts((current) => ({
      ...current,
      [`${rowId}_${dateKey}`]: formatInlineTimeInput(value),
    }));
  };

  const handleWorkCellBlur = (item, day, index) => {
    const dateKey = day.format('YYYY-MM-DD');
    const draftKey = `${item.id}_${dateKey}`;
    const draftValue = cellDrafts[draftKey];

    if (_.isUndefined(draftValue)) {
      return;
    }

    const existingMinutes = item.days?.[index] || 0;
    const parsedMinutes = parseInlineTimeInputToMinutes(draftValue);
    const workItemEntryId = getWorkItemEntryIdForDay(item, day, index);

    if (_.isNull(parsedMinutes) || parsedMinutes === existingMinutes) {
      setCellDrafts((current) => _.omit(current, draftKey));
      return;
    }

    const optimisticEntryId = parsedMinutes === 0 ? 0 : workItemEntryId;

    setWorkItems((current) =>
      _.map(current, (currentItem) =>
        currentItem.id === item.id
          ? updateItemDay(
              currentItem,
              dateKey,
              index,
              parsedMinutes,
              'id',
              optimisticEntryId,
            )
          : currentItem,
      ),
    );
    setCellDrafts((current) =>
      current[draftKey] === draftValue ? _.omit(current, draftKey) : current,
    );

    editTimesheet(workItemEntryId, {
      user_id: Number(selectedUserId) || Number(auth?.id),
      date: dateKey,
      project_id: item.project_id,
      stage_id: item.stage_id,
      task_id: item.task_id || null,
      substage_id: item.substage_id || null,
      minutes: parsedMinutes,
      is_billable: item.billable ? 1 : 0,
    }).then((action) => {
      const savedEntry = getSavedEntryFromAction(action, dateKey);
      const savedEntryId =
        parsedMinutes === 0 ? 0 : savedEntry?.id || workItemEntryId;

      if (String(savedEntryId) !== String(optimisticEntryId)) {
        setWorkItems((current) =>
          _.map(current, (currentItem) =>
            currentItem.id === item.id &&
            currentItem.days?.[index] === parsedMinutes
              ? updateItemDayIdentifier(
                  currentItem,
                  dateKey,
                  'id',
                  savedEntryId,
                )
              : currentItem,
          ),
        );
      }

      refreshWeekViewStatistics();
    })
      .catch(() => {
        setWorkItems((current) =>
          _.map(current, (currentItem) =>
            currentItem.id === item.id &&
            currentItem.days?.[index] === parsedMinutes
              ? updateItemDay(
                  currentItem,
                  dateKey,
                  index,
                  existingMinutes,
                  'id',
                  workItemEntryId,
                )
              : currentItem,
          ),
        );
      });
  };

  const handleTimeOffCellBlur = (item, day, index) => {
    const dateKey = day.format('YYYY-MM-DD');
    const draftKey = `${item.id}_${dateKey}`;
    const draftValue = cellDrafts[draftKey];

    if (_.isUndefined(draftValue)) {
      return;
    }

    const existingMinutes = item.days?.[index] || 0;
    const parsedMinutes = parseInlineTimeInputToMinutes(draftValue);
    const timeOffEntryUuid = getTimeOffEntryUuidForDay(item, day);

    if (_.isNull(parsedMinutes) || parsedMinutes === existingMinutes) {
      setCellDrafts((current) => _.omit(current, draftKey));
      return;
    }

    const optimisticEntryUuid =
      parsedMinutes === 0 ? 'null' : timeOffEntryUuid;

    setTimeOffItems((current) =>
      _.map(current, (currentItem) =>
        currentItem.id === item.id
          ? updateItemDay(
              currentItem,
              dateKey,
              index,
              parsedMinutes,
              'leave_uuid',
              optimisticEntryUuid,
            )
          : currentItem,
      ),
    );
    setCellDrafts((current) =>
      current[draftKey] === draftValue ? _.omit(current, draftKey) : current,
    );

    editUserLeave(timeOffEntryUuid, {
      user_id: Number(selectedUserId) || Number(auth?.id),
      reason: item.leave_reason_id || '',
      description: item.description || '',
      date: dateKey,
      minutes: parsedMinutes,
    }).then((action) => {
      const savedEntry = getSavedEntryFromAction(action, dateKey);
      const savedEntryUuid =
        parsedMinutes === 0
          ? 'null'
          : savedEntry?.leave_uuid || timeOffEntryUuid;

      setTimeOffItems((current) =>
        _.reduce(
          current,
          (result, currentItem) => {
            if (currentItem.id !== item.id) {
              return [...result, currentItem];
            }

            const updatedItem =
              currentItem.days?.[index] === parsedMinutes
                ? updateItemDayIdentifier(
                    currentItem,
                    dateKey,
                    'leave_uuid',
                    savedEntryUuid,
                  )
                : currentItem;

            return _.sum(updatedItem.days) > 0
              ? [...result, updatedItem]
              : result;
          },
          [],
        ),
      );

      refreshWeekViewStatistics();
    })
      .catch(() => {
        setTimeOffItems((current) =>
          _.map(current, (currentItem) =>
            currentItem.id === item.id &&
            currentItem.days?.[index] === parsedMinutes
              ? updateItemDay(
                  currentItem,
                  dateKey,
                  index,
                  existingMinutes,
                  'leave_uuid',
                  timeOffEntryUuid,
                )
              : currentItem,
          ),
        );
      });
  };

  return (
    <div className={styles.page}>
      {showAddWorkItemModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowAddWorkItemModal(false)}
        >
          <div
            className={styles.modalCard}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                {__('wizard.add-timesheet.title.create-new')}
              </div>
              <div className={styles.modalSubtitle}>
                {__('wizard.timesheet.title.time-entry')}
              </div>
            </div>
            <div className={styles.modalBody}>
              <AddTimesheetEntryWorkItemForm
                initialValues={{
                  date: activeDateRange,
                  project_id: activeProject,
                  stage_id: activeStage,
                }}
                onClose={() => setShowAddWorkItemModal(false)}
                onSubmit={handleCreateWorkItem}
              />
            </div>
          </div>
        </div>
      )}

      {showAddTimeOffModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowAddTimeOffModal(false)}
        >
          <div
            className={styles.modalCard}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                {__('wizard.add-timesheet.title.create-new')}
              </div>
              <div className={styles.modalSubtitle}>
                {__('wizard.timesheet.title.time-off-entry')}
              </div>
            </div>
            <div className={styles.modalBody}>
              <AddTimesheetEntryTimeOffForm
                onClose={() => setShowAddTimeOffModal(false)}
                onSubmit={handleCreateTimeOffItem}
                selectedUserId={selectedUserId}
              />
            </div>
          </div>
        </div>
      )}

      {showEditWorkItemModal && (
        <div className={styles.modalOverlay} onClick={closeEditWorkItemModal}>
          <div
            className={styles.modalCard}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                {__('wizard.edit-timesheet.title.edit')}
              </div>
              <div className={styles.modalSubtitle}>
                {__('wizard.timesheet.title.time-entry')}
              </div>
            </div>
            <div className={styles.modalBody}>
              {editWorkItemInitialValues && (
                <EditTimesheetEntryWorkItemForm
                  initialValues={editWorkItemInitialValues}
                  onClose={closeEditWorkItemModal}
                  onSubmit={handleEditWorkItem}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showEditTimeOffModal && (
        <div className={styles.modalOverlay} onClick={closeEditTimeOffModal}>
          <div
            className={styles.modalCard}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                {__('wizard.edit-timesheet.title.edit')}
              </div>
              <div className={styles.modalSubtitle}>
                {__('wizard.timesheet.title.time-off-entry')}
              </div>
            </div>
            <div className={styles.modalBody}>
              {editTimeOffInitialValues && (
                <EditTimesheetEntryTimeOffForm
                  initialValues={editTimeOffInitialValues}
                  onClose={closeEditTimeOffModal}
                  onSubmit={handleEditTimeOff}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <TimesheetEntriesHeader
        users={possibleUsers}
        selectedUserId={selectedUserId}
        onUserChange={(value) => setSelectedUserId(Number(value))}
        weekLabel={`${__(
          'timesheet_entries.header.week',
        )} ${currentWeekNumber}`}
        weekRange={formatRange(weekStart)}
        prevWeekLabel={`<${__('timesheet_entries.header.week_short')} ${moment(
          weekStart,
        )
          .clone()
          .subtract(1, 'week')
          .isoWeek()}`}
        nextWeekLabel={`${__('timesheet_entries.header.week_short')} ${moment(
          weekStart,
        )
          .clone()
          .add(1, 'week')
          .isoWeek()}>`}
        onPrevWeek={() =>
          setWeekStart((current) => moment(current).clone().subtract(1, 'week'))
        }
        onToday={() => setWeekStart(moment().startOf('isoWeek'))}
        onNextWeek={() =>
          setWeekStart((current) => moment(current).clone().add(1, 'week'))
        }
        viewModes={viewModes}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      <div className={styles.shell}>
        <TimesheetEntriesStatistic
          statisticsData={statisticsData}
          formatMinutes={formatMinutes}
        />

        <div className={styles.panel}>
          <button
            type='button'
            className={styles.sectionToggle}
            onClick={() => toggleSection('time')}
          >
            <ArrowDownSmallIcon
              className={classNames(
                styles.sectionArrow,
                _.includes(collapsedSections, 'time') &&
                  styles.sectionArrowCollapsed,
              )}
            />
            <span>{__('Time')}</span>
          </button>

          {!_.includes(collapsedSections, 'time') && (
            <>
              <div className={styles.tableHeader} style={tableGridStyle}>
                <div
                  className={classNames(styles.headerCell, styles.projectCell)}
                >
                  {__('Project / Stage')}
                </div>
                <div className={classNames(styles.headerCell, styles.taskCell)}>
                  {__('Substage / Task')}
                </div>
                {_.map(visibleDays, (day, index) => {
                  const dateKey = day.format('YYYY-MM-DD');
                  const holidayName = holidayMap?.[dateKey]?.holiday_name;

                  return (
                    <div
                      key={dateKey}
                      className={classNames(
                        styles.headerCell,
                        styles.dayCell,
                        holidayColumns[index] && styles.dayCellHoliday,
                      )}
                    >
                      <div className={styles.dayHeaderContent}>
                        {holidayColumns[index] && holidayName && (
                          <span className={styles.holidayName}>
                            {holidayName}
                          </span>
                        )}
                        <span
                          className={classNames(
                            styles.dayLabel,
                            day.isSame(moment(), 'day') && styles.dayLabelToday,
                          )}
                        >
                          {`${getTranslatedWeekdayShort(day)} ${day.format(
                            'D',
                          )}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div
                  className={classNames(styles.headerCell, styles.totalCell)}
                >
                  {__('timesheet_entries.table.total')}
                </div>
                <div
                  className={classNames(styles.headerCell, styles.actionsCell)}
                >
                  {__('timesheet_entries.table.actions')}
                </div>
              </div>

              <div className={styles.dailyTotalRow} style={tableGridStyle}>
                <div
                  className={classNames(
                    styles.entryCell,
                    styles.projectCell,
                    styles.dailyTotal,
                  )}
                >
                  {__('Daily total')}
                </div>
                <div
                  className={classNames(styles.entryCell, styles.taskCell)}
                />
                {_.map(dailyTotals, (minutes, index) => (
                  <div
                    key={index}
                    className={classNames(
                      styles.entryCell,
                      styles.dayCell,
                      holidayColumns[index] && styles.dayCellHoliday,
                    )}
                  >
                    {minutes ? formatMinutesWithLeadingHour(minutes) : '-'}
                  </div>
                ))}
                <div
                  className={classNames(
                    styles.entryCell,
                    styles.dayCell,
                    styles.dailyTotalValue,
                  )}
                >
                  {formatMinutesWithLeadingHour(_.sum(dailyTotals))}
                </div>
                <div
                  className={classNames(styles.entryCell, styles.actionsCell)}
                />
              </div>

              <div className={styles.groupBlock}>
                <div className={styles.groupTitle}>{__('Time Work')}</div>
                {_.map(workItems, (item) => {
                  const total = _.sumBy(
                    visibleIndexes,
                    (index) => item.days?.[index] || 0,
                  );
                  const hasLoggedTime = total > 0;
                  const workItemEditDisabledTooltip = __(
                    'timesheet-entries.tooltip.multiple-entries-edit-disabled',
                  );
                  const rowActionsTooltip = hasLoggedTime
                    ? __('timesheet-entries.tooltip.row-actions-disabled')
                    : __('content.tooltip.table.rowactions');

                  return (
                    <div
                      key={item.actionKey || item.id}
                      className={styles.entryRow}
                      style={tableGridStyle}
                    >
                      <div
                        className={classNames(
                          styles.entryCell,
                          styles.projectCell,
                        )}
                      >
                        <span className={styles.rowAccent}>
                          <span
                            className={classNames(
                              styles.rowAccentLine,
                              styles[`accent${_.upperFirst(item.color)}`],
                            )}
                          />
                        </span>
                        <span>{item.projectStage}</span>
                      </div>
                      <div
                        className={classNames(
                          styles.entryCell,
                          styles.taskCell,
                        )}
                      >
                        {/* <span className={styles.substageTask}>

                        </span> */}
                        {item.substageTask}
                      </div>
                      {_.map(visibleIndexes, (index) =>
                        (() => {
                          const dateKey =
                            visibleDays[index].format('YYYY-MM-DD');
                          const draftKey = `${item.id}_${dateKey}`;
                          const canEditWorkItem =
                            !getWorkItemMultipleEntriesForDay(
                              item,
                              visibleDays[index],
                              index,
                            );
                          const cellValue =
                            cellDrafts[draftKey] ??
                            (item.days?.[index]
                              ? formatInlineMinutesValue(item.days[index])
                              : '');

                          return (
                            <div
                              key={index}
                              className={classNames(
                                styles.entryCell,
                                styles.dayCell,
                                holidayColumns[index] && styles.dayCellHoliday,
                              )}
                            >
                              <div className={styles.workTimeCell}>
                                {canEditWorkItem ? (
                                  <input
                                    type='text'
                                    className={styles.timeInput}
                                    value={cellValue}
                                    placeholder='-'
                                    onChange={(event) =>
                                      handleCellDraftChange(
                                        item.id,
                                        dateKey,
                                        event.target.value,
                                      )
                                    }
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        event.currentTarget.blur();
                                      }
                                    }}
                                    onBlur={() =>
                                      handleWorkCellBlur(
                                        item,
                                        visibleDays[index],
                                        index,
                                      )
                                    }
                                  />
                                ) : (
                                  <span className={styles.timeDisplayValue}>
                                    <Tooltip text={workItemEditDisabledTooltip}>
                                      <span
                                        className={classNames(
                                          styles.timeDisplayValueDisabled,
                                          !cellValue &&
                                            styles.timeDisplayValueEmptyDisabled,
                                        )}
                                      >
                                        {cellValue || '-'}
                                      </span>
                                    </Tooltip>
                                  </span>
                                )}
                                {canEditWorkItem ? (
                                  <button
                                    type='button'
                                    className={styles.editButton}
                                    onMouseDown={(event) =>
                                      event.preventDefault()
                                    }
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();

                                      if (!canEditWorkItem) {
                                        return;
                                      }

                                      handleOpenEditWorkItemModal(
                                        item,
                                        visibleDays[index],
                                        index,
                                      );
                                    }}
                                  >
                                    <EditIcon className={styles.editIcon} />
                                  </button>
                                ) : (
                                  <Tooltip text={workItemEditDisabledTooltip}>
                                    <button
                                      type='button'
                                      className={classNames(
                                        styles.editButton,
                                        styles.editButtonDisabled,
                                      )}
                                      onMouseDown={(event) =>
                                        event.preventDefault()
                                      }
                                      onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                      }}
                                    >
                                      <EditIcon className={styles.editIcon} />
                                    </button>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          );
                        })(),
                      )}
                      <div
                        className={classNames(
                          styles.entryCell,
                          styles.totalCell,
                        )}
                      >
                        {formatMinutesWithLeadingHour(total)}
                      </div>
                      <div
                        className={classNames(
                          styles.entryCell,
                          styles.actionsCell,
                        )}
                      >
                        <div className={styles.rowActions}>
                          <Tooltip text={rowActionsTooltip}>
                            <button
                              type='button'
                              className={classNames(
                                styles.rowActionsTrigger,
                                hasLoggedTime &&
                                  styles.rowActionsTriggerDisabled,
                              )}
                              aria-disabled={hasLoggedTime}
                              onClick={(event) => {
                                if (hasLoggedTime) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  return;
                                }

                                toggleRowActions(`work-${item.id}`, event);
                              }}
                            >
                              <MenuHIcon
                                className={classNames(
                                  styles.rowActionsIcon,
                                  hasLoggedTime &&
                                    styles.rowActionsIconDisabled,
                                  actionedRowId === `work-${item.id}` &&
                                    styles.rowActionsIconActive,
                                )}
                              />
                            </button>
                          </Tooltip>
                          <div
                            className={classNames(
                              styles.rowActionsMenu,
                              actionedRowId === `work-${item.id}` &&
                                styles.rowActionsMenuActive,
                            )}
                          >
                            <Tooltip text={__('Delete')}>
                              <button
                                type='button'
                                className={styles.rowActionButton}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  handleDeleteWorkItem(item);
                                }}
                              >
                                <DeleteIcon
                                  className={styles.rowActionDeleteIcon}
                                />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* TIME OFF */}
              <div className={styles.groupBlock}>
                <div className={styles.groupTitle}>{__('Time Off')}</div>
                {_.map(timeOffItems, (item) => {
                  const total = _.sumBy(
                    visibleIndexes,
                    (index) => item.days?.[index] || 0,
                  );
                  const hasLoggedTime = total > 0;
                  const rowActionsTooltip = hasLoggedTime
                    ? __('timesheet-entries.tooltip.row-actions-disabled')
                    : __('content.tooltip.table.rowactions');

                  return (
                    <div
                      key={item.id}
                      className={styles.entryRow}
                      style={tableGridStyle}
                    >
                      <div
                        className={classNames(
                          styles.entryCell,
                          styles.projectCell,
                        )}
                      >
                        <span className={styles.rowAccent}>
                          <span
                            className={classNames(
                              styles.rowAccentLine,
                              styles[`accent${_.upperFirst(item.color)}`],
                            )}
                          />
                        </span>
                        <span>{item.projectStage}</span>
                      </div>
                      <div
                        className={classNames(
                          styles.entryCell,
                          styles.taskCell,
                        )}
                      >
                        {item.substageTask}
                      </div>
                      {_.map(visibleIndexes, (index) => {
                        const currentDay = visibleDays[index];
                        const dateKey = currentDay.format('YYYY-MM-DD');
                        const draftKey = `${item.id}_${dateKey}`;
                        const timeOffDayValue = item.days?.[index]
                          ? formatInlineMinutesValue(item.days[index])
                          : '-';
                        const hasMultipleEntries =
                          getTimeOffMultipleEntriesForDay(item, currentDay);
                        const isAllDayTimeOff = Number(item?.all_day) === 1;
                        const canInlineEditTimeOff =
                          !isAllDayTimeOff && !hasMultipleEntries;
                        const canModalEditTimeOff = !hasMultipleEntries;
                        const timeOffInlineEditDisabledTooltip =
                          hasMultipleEntries
                            ? __(
                                'timesheet-entries.tooltip.multiple-entries-edit-disabled',
                              )
                            : __(
                                'timesheet-entries.tooltip.all-day-edit-disabled',
                              );

                        return (
                          <div
                            key={index}
                            className={classNames(
                              styles.entryCell,
                              styles.dayCell,
                              holidayColumns[index] && styles.dayCellHoliday,
                            )}
                          >
                            <div className={styles.workTimeCell}>
                              {canInlineEditTimeOff ? (
                                <input
                                  type='text'
                                  className={styles.timeInput}
                                  value={
                                    cellDrafts[draftKey] ??
                                    (item.days?.[index]
                                      ? formatInlineMinutesValue(
                                          item.days[index],
                                        )
                                      : '')
                                  }
                                  placeholder='-'
                                  onChange={(event) =>
                                    handleCellDraftChange(
                                      item.id,
                                      dateKey,
                                      event.target.value,
                                    )
                                  }
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                      event.currentTarget.blur();
                                    }
                                  }}
                                  onBlur={() =>
                                    handleTimeOffCellBlur(
                                      item,
                                      currentDay,
                                      index,
                                    )
                                  }
                                />
                              ) : (
                                <span className={styles.timeDisplayValue}>
                                  <Tooltip
                                    text={timeOffInlineEditDisabledTooltip}
                                  >
                                    <span
                                      className={classNames(
                                        styles.timeDisplayValueDisabled,
                                        timeOffDayValue === '-' &&
                                          styles.timeDisplayValueEmptyDisabled,
                                      )}
                                    >
                                      {timeOffDayValue}
                                    </span>
                                  </Tooltip>
                                </span>
                              )}
                              {canModalEditTimeOff ? (
                                <button
                                  type='button'
                                  className={styles.editButton}
                                  onMouseDown={(event) =>
                                    event.preventDefault()
                                  }
                                  onClick={(event) =>
                                    handleOpenEditTimeOffModal(
                                      item,
                                      currentDay,
                                      index,
                                      event,
                                    )
                                  }
                                >
                                  <EditIcon className={styles.editIcon} />
                                </button>
                              ) : (
                                <Tooltip
                                  text={__(
                                    'timesheet-entries.tooltip.multiple-entries-edit-disabled',
                                  )}
                                >
                                  <button
                                    type='button'
                                    className={classNames(
                                      styles.editButton,
                                      styles.editButtonDisabled,
                                    )}
                                    onMouseDown={(event) =>
                                      event.preventDefault()
                                    }
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }}
                                  >
                                    <EditIcon className={styles.editIcon} />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div
                        className={classNames(
                          styles.entryCell,
                          styles.totalCell,
                        )}
                      >
                        {formatMinutesWithLeadingHour(total)}
                      </div>
                      <div
                        className={classNames(
                          styles.entryCell,
                          styles.actionsCell,
                        )}
                      >
                        <div className={styles.rowActions}>
                          <Tooltip text={rowActionsTooltip}>
                            <button
                              type='button'
                              className={classNames(
                                styles.rowActionsTrigger,
                                hasLoggedTime &&
                                  styles.rowActionsTriggerDisabled,
                              )}
                              aria-disabled={hasLoggedTime}
                              onClick={(event) => {
                                if (hasLoggedTime) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  return;
                                }

                                toggleRowActions(item.actionKey, event);
                              }}
                            >
                              <MenuHIcon
                                className={classNames(
                                  styles.rowActionsIcon,
                                  hasLoggedTime &&
                                    styles.rowActionsIconDisabled,
                                  actionedRowId === item.actionKey &&
                                    styles.rowActionsIconActive,
                                )}
                              />
                            </button>
                          </Tooltip>
                          <div
                            className={classNames(
                              styles.rowActionsMenu,
                              actionedRowId === item.actionKey &&
                                styles.rowActionsMenuActive,
                            )}
                          >
                            <Tooltip text={__('Delete')}>
                              <button
                                type='button'
                                className={styles.rowActionButton}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  handleDeleteTimeOffItem(item);
                                }}
                              >
                                <DeleteIcon
                                  className={styles.rowActionDeleteIcon}
                                />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={styles.footerActions}>
                <div className={styles.footerLeft}>
                  <button type='button' onClick={addWorkItem}>
                    {__('Add Work Item')}
                  </button>
                  <button type='button' onClick={handleCopyPreviousWeek}>
                    {__('Copy From Previous Week')}
                  </button>
                  <button type='button' onClick={addTimeOff}>
                    {__('Add Time Off')}
                  </button>
                </div>
                {/* <button type='button' className={styles.submitButton}>
                  {__('Submit For Approval')}
                </button> */}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default connect((state) => ({
  auth: state.auth,
}))(TimesheetEntries);
